/**
 * Site-wide motion enhancements, kept deliberately small:
 * - Reveal: fades and slides in `[data-reveal]` elements (and whole sections without
 *   their own reveal targets) as they enter the viewport, staggered per batch.
 *   Only active when the layout added `html.motion-reveal` (setting on, motion allowed).
 * - Count-up: animates `[data-count-to]` numbers once they are revealed.
 * - In-view: toggles `.is-in-view` on `[data-loop-animation]` so looping CSS
 *   animations only run while visible.
 *
 * Content is fully visible without this script; the layout's inline guard removes
 * `motion-reveal` if this module never loads.
 */

const root = document.documentElement;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const REVEAL_SELECTOR =
  '[data-reveal], .content-for-layout > .shopify-section:not(:first-child):not(:has([data-reveal]))';
const STAGGER_MS = 90;
const MAX_STAGGER_STEPS = 6;
const COUNT_DURATION_MS = 1600;

root.dataset.motionReady = 'true';

/**
 * Respect reduced motion for autoplaying background videos (e.g. the hero): pause them
 * and drop autoplay so they show their first frame instead.
 */
function pauseAutoplayVideos(scope = document) {
  if (!reducedMotion.matches) return;
  for (const video of scope.querySelectorAll('video[autoplay]')) {
    video.removeAttribute('autoplay');
    video.pause();
  }
}
pauseAutoplayVideos();

/** @type {WeakSet<Element>} */
const observed = new WeakSet();

const revealObserver = new IntersectionObserver(handleReveal, {
  rootMargin: '0px 0px -10% 0px',
  threshold: 0.12,
});

const loopObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) entry.target.classList.toggle('is-in-view', entry.isIntersecting);
  },
  { threshold: 0.2 }
);

/**
 * Reveals the intersecting elements of one batch in visual order with a small delay
 * between each, then stops observing them.
 * @param {IntersectionObserverEntry[]} entries
 */
function handleReveal(entries) {
  const visible = entries
    .filter((entry) => entry.isIntersecting)
    .map((entry) => entry.target)
    .sort((a, b) => {
      const ra = a.getBoundingClientRect();
      const rb = b.getBoundingClientRect();
      return ra.top - rb.top || ra.left - rb.left;
    });

  let index = 0;
  for (const element of visible) {
    if (!(element instanceof HTMLElement)) continue;
    element.style.setProperty('--reveal-delay', `${Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS}ms`);
    element.classList.add('is-revealed');
    revealObserver.unobserve(element);
    index += 1;

    for (const counter of element.querySelectorAll('[data-count-to]')) countUp(counter);
    if (element.matches('[data-count-to]')) countUp(element);
  }
}

/**
 * Counts a number up from zero to its `data-count-to` value.
 * The final value is already in the markup, so this is purely visual.
 * @param {Element} element
 */
function countUp(element) {
  if (!(element instanceof HTMLElement) || element.dataset.counted) return;
  element.dataset.counted = 'true';

  const target = Number(element.dataset.countTo);
  if (!Number.isFinite(target) || reducedMotion.matches) return;

  const decimals = (element.dataset.countTo?.split('.')[1] ?? '').length;
  const formatter = new Intl.NumberFormat(root.lang || undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  const delay = parseFloat(element.closest('.is-revealed')?.style.getPropertyValue('--reveal-delay') || '0');
  let start = 0;

  /** @param {number} now */
  const step = (now) => {
    if (!start) start = now;
    const progress = Math.min((now - start) / COUNT_DURATION_MS, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    element.textContent = formatter.format(target * eased);
    if (progress < 1) requestAnimationFrame(step);
  };

  element.textContent = formatter.format(0);
  setTimeout(() => requestAnimationFrame(step), delay);
}

/**
 * Starts observing reveal and loop targets inside a root. Safe to call repeatedly.
 * @param {ParentNode} scope
 */
function scan(scope = document) {
  if (root.classList.contains('motion-reveal')) {
    for (const element of scope.querySelectorAll(REVEAL_SELECTOR)) {
      if (observed.has(element)) continue;
      observed.add(element);
      revealObserver.observe(element);
    }
  }

  for (const element of scope.querySelectorAll('[data-loop-animation]')) {
    if (observed.has(element)) continue;
    observed.add(element);
    loopObserver.observe(element);
  }
}

scan();

// Theme editor: sections are re-rendered in place, so observe their new content.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    scan(event.target);
    pauseAutoplayVideos(event.target);
  }
});
